import { AlertTriangle, CheckCircle2, Info, X, XCircle, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './Banner.module.css';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger' | 'accent';

const TONE_ICON: Record<BannerTone, LucideIcon | null> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
  accent: null,
};

export type BannerProps = {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** An illustration or emoji to lead with instead of the tone icon. */
  art?: ReactNode;
  actions?: ReactNode;
  /** Show a close button. */
  onDismiss?: () => void;
  className?: string;
};

/** An in-page message. Informational (role=status): nothing here should steal focus. */
export function Banner({
  tone = 'info',
  title,
  children,
  art,
  actions,
  onDismiss,
  className,
}: BannerProps) {
  const icon = TONE_ICON[tone];
  return (
    <div className={cx(s.banner, className)} data-tone={tone} role="status">
      {art ? (
        <span className={s.art} aria-hidden="true">
          {art}
        </span>
      ) : icon ? (
        <Icon icon={icon} size="lg" className={s.icon} />
      ) : null}
      <div className={s.body}>
        {title ? <p className={s.title}>{title}</p> : null}
        {children ? <div className={s.text}>{children}</div> : null}
        {actions ? <div className={s.actions}>{actions}</div> : null}
      </div>
      {onDismiss ? (
        <button type="button" className={s.dismiss} aria-label="Dismiss" onClick={onDismiss}>
          <Icon icon={X} size="md" />
        </button>
      ) : null}
    </div>
  );
}
